import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Button, Row, Col, Panel } from 'react-bootstrap';
import FormInput from 'components/forms/FormInput';
import FormTags from 'components/forms/FormTags';
import Loader from 'components/layout/Loader';
import FlyerAlarmVariantsList from './FlyerAlarmVariantsList';

/**
 * Internally used sub-component to display loading or loaded options to pick from.
 * Arguments are 2 objects with similar structure. Keys are option ids, values have keys "name" and "options",
 * where options is also an object with optionId keys and {name, description, price, currency} values.
 *
 * Depending on known whitelist and blacklist option ids, each option is either in "perforation" or "other" options.
 * @see VendorBundle\Service\FlyerAlarmOptionsPriceFetcher
 *
 * @param {Object} perforationOptions
 * @param {Object} otherOptions
 */
const FlyerAlarmOptionsDisplay = ({ perforationOptions, otherOptions, isLoading, addOptionId }) => {
  const {
    messages: { perforation: text },
  } = useIntl();
  const perforationCount = Object.keys(perforationOptions).length;
  const otherCount = Object.keys(otherOptions).length;

  if (isLoading) return <Loader />;

  if (perforationCount === 0 && otherCount === 0) return null;

  return (
    <>
      <h4>
        {perforationCount + otherCount} {text.options}
      </h4>
      <p>{text.select_option_value}.</p>
      <Panel>
        <Panel.Heading>
          <Panel.Title>
            {perforationCount} {text.perforation_options}
          </Panel.Title>
        </Panel.Heading>
        <Panel.Body>
          {perforationCount > 0 ? (
            <Options options={perforationOptions} addOptionId={addOptionId} />
          ) : (
            <p>{text.please_try_other}</p>
          )}
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Heading>
          <Panel.Title toggle>
            {otherCount} {text.other_options}
          </Panel.Title>
        </Panel.Heading>
        <Panel.Collapse>
          <Panel.Body>
            <Options options={otherOptions} addOptionId={addOptionId} />
          </Panel.Body>
        </Panel.Collapse>
      </Panel>
    </>
  );
};

const Options = ({ options, addOptionId }) =>
  Object.entries(options).map(([optionId, { name, options }]) => (
    <SingleOption key={optionId} id={optionId} name={name} options={options} addOptionId={addOptionId} />
  ));

const SingleOption = ({ id, name, options, addOptionId }) => {
  return (
    <>
      <p>
        &laquo;{id}&raquo; {name}:
      </p>
      <div>
        {Object.entries(options).map(([optionId, { name, description }]) => (
          <div key={optionId}>
            <button type="button" title={description} href="#" onClick={() => addOptionId(id, optionId)}>
              {optionId}
            </button>{' '}
            {name}
          </div>
        ))}
      </div>
    </>
  );
};

/**
 * Serialize an object with selected options and their selected option values.
 * In example, {"34": 34001, "54": 54002} becomes "34:34001;54:54002"
 * @param {Object} obj dictionary with option ids and corresponding optionOptionIds.
 * @returns string
 */
const serialize = obj =>
  Object.entries(obj)
    .map(([key, value]) => `${key}:${value}`)
    .join(';');

/**
 * Parse stored string like "34:34001;54:54002" into object {"34": 34001, "54": 54002}
 * @param {string} str saved string with option ids and corresponding optionOptionIds.
 * @returns Object
 */
const deserialize = str =>
  str
    ? str.split(';').reduce((acc, c) => {
        const [id, valueId] = c.split(':');
        if (!id || !valueId) return acc;
        acc[id] = valueId;
        return acc;
      }, {})
    : {};

/**
 * UI to explore FlyerAlarm options available.
 * Lets query FlyerAlarm API for product group, variants and quantities
 * and pick certain perforation options as preselected
 */
const FlyerAlarmOptionsSelector = ({ value, handleSelect, countries, vendorId }) => {
  const availableCountries = countries?.map?.(({ country }) => String(country).toLowerCase()) ?? [];
  console.log({ availableCountries });
  const initialOptionIds = deserialize(value);

  const [optionIds, setOptionIds] = useState(initialOptionIds);
  const addOptionId = (id, optionId) => {
    setOptionIds({ ...optionIds, [id]: optionId });
  };
  const [productGroupId, setProductGroupId] = useState(vendorId ?? '');
  const [disabled, setDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [productGroupData, setProductGroupData] = useState();

  const [whitelist, setWhitelist] = useState([]);
  const [blacklist, setBlacklist] = useState([]);

  // lookupOptions
  const [perforationOptions, setPerforationOptions] = useState({});
  const [otherOptions, setOtherOptions] = useState({});
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);

  const { messages } = useIntl();

  /**
   * Fetch whitelist and blacklist from server
   */
  useEffect(() => {
    const fetcher = name => fetch(`/api/flyeralarm/catalog/${name}`);
    Promise.all([fetcher('whitelist'), fetcher('blacklist')]).then(async ([whitelistResponse, blacklistResponse]) => {
      const whitelist = await whitelistResponse.json();
      const blacklist = await blacklistResponse.json();
      setWhitelist(whitelist.map(String));
      setBlacklist(blacklist.map(String));
    });
  }, []);

  const fetchVariants = async productGroupId => {
    // reset options display
    setPerforationOptions({});
    setOtherOptions({});
    setIsOptionsLoading(false);

    setDisabled(true);
    setIsLoading(true);

    const response = await fetch(`/api/flyeralarm/catalog/product-group/${productGroupId}`);
    const json = await response.json();
    setDisabled(false);
    setIsLoading(false);
    setProductGroupData(json);
  };

  /**
   * Query FlyerAlarm API for options available for certain quantity and shipping type.
   * @param {string|int} quantityId
   * @param {string} shippingTypeId
   */
  const lookupOptions = async (quantityId, shippingTypeId) => {
    setIsOptionsLoading(true);
    setPerforationOptions({});
    setOtherOptions({});

    const response = await fetch(`/api/flyeralarm/catalog/options/${quantityId}/${shippingTypeId}`);
    const json = await response.json();
    const perforationOptions = Object.fromEntries(Object.entries(json).filter(([key]) => !blacklist.includes(key)));
    const otherOptions = Object.fromEntries(Object.entries(json).filter(([key]) => !whitelist.includes(key)));

    setPerforationOptions(perforationOptions);
    setOtherOptions(otherOptions);

    setIsOptionsLoading(false);
  };

  const updateOptionIds = (name, values) => {
    const newOptionIds = Object.fromEntries(
      values.map(
        v => v.split(':') // .map(([key, value]) => [String(key), Number(value)])
      )
    );
    setOptionIds(newOptionIds);
    console.log('updateOptionIds', { name, values, newOptionIds });
  };

  const onFinishedSelecting = () => {
    handleSelect(serialize(optionIds));
  };

  const tagValues = Object.entries(optionIds).map(([id, valueId]) => `${id}:${valueId}`);
  console.log({ tagValues, optionIds });

  return (
    <>
      <Row>
        <Col xs={12} md={8}>
          <p>{messages.perforation.how_to}</p>
        </Col>
        <Col xs={12} md={4}>
          <FormTags
            label={messages.perforation.selected_options}
            name="options"
            value={tagValues}
            onChange={updateOptionIds}
            messages={messages}
            disabled={disabled}
          />
          <Button onClick={onFinishedSelecting} bsStyle="primary">
            <i className="far fa-save" /> <span> {messages.perforation.set_perforation_options}</span>
          </Button>
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={4}>
          <FormInput
            name="productGroupId"
            value={productGroupId}
            onChange={(name, value) => {
              setProductGroupId(value);
            }}
            label="FlyerAlarm product group id"
            placeholder={productGroupId ?? '7050'}
          />
          <Button
            onClick={() => {
              fetchVariants(productGroupId);
            }}
            disabled={isLoading}
          >
            <i className={isLoading ? 'fa fa-circle-notch fa-spin' : 'far fa-search'} />
            {messages.search}
          </Button>
        </Col>
      </Row>
      <FlyerAlarmVariantsList productGroupData={productGroupData} lookupOptions={lookupOptions}>
        <FlyerAlarmOptionsDisplay
          perforationOptions={perforationOptions}
          otherOptions={otherOptions}
          isLoading={isOptionsLoading}
          addOptionId={addOptionId}
        />
      </FlyerAlarmVariantsList>
    </>
  );
};

export default FlyerAlarmOptionsSelector;
